<template>
  <div
    class="taunt-container"
    :class="{ 'taunt-container--full-panel': isPanelVisible }"
  >
    <!-- Current Active Taunt -->
    <transition name="slide-right">
      <div
        v-if="!showingFullPanel && hasActiveMessage"
        class="taunt-active-message"
      >
        <taunt-message
          single-view
          :taunt="activeMessage"
          mode="received"
          @buttonClicked="handleGoToAuction"
          @closeClicked="closeActiveMessage"
        />
      </div>
    </transition>

    <!-- Listing of Taunts -->
    <transition name="slide-side-panel">
      <div
        v-if="showingFullPanel"
        v-on-clickAway:mousedown="handleClickAway"
        class="taunt-full-panel"
      >
        <div class="taunt-full-panel__header">
          <h5>
            Taunts
          </h5>
          <acv-icon-button
            :show-tooltip="false"
            icon-type="close"
            @click="handlePanelShow(false)"
          />
        </div>
        <div class="taunt-modes">
          <button
            type="button"
            :class="{
              selected: mode === 'received',
            }"
            @click="mode = 'received'"
          >
            <acv-icon icon="move_to_inbox" /> Received Taunts
          </button>
          <button
            type="button"
            :class="{
              selected: mode === 'sent',
            }"
            @click="mode = 'sent'"
          >
            Sent Taunts <acv-icon icon="outbox" />
          </button>
        </div>
        <div class="taunt-full-panel__content">
          <div class="taunt-list">
            <taunt-message
              v-for="(taunt, index) in tauntsToShow"
              :key="index"
              :taunt="taunt"
              tabindex="0"
              :mode="mode"
              @buttonClicked="handleGoToAuction"
              @closeClicked="removeMessage(taunt.id)"
              @mouseenter.native="markAsSeen(taunt.id)"
              @focusin.native="markAsSeen(taunt.id)"
              @counterEnded="removeMessage(taunt.id)"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { directive as onClickAway } from 'vue-clickaway2';
import { AcvIconButton, AcvIcon } from '@acv/shared-vuejs';
import TauntMessage from './TauntMessage.vue';

export default {
  name: 'TauntPanel',
  components: {
    TauntMessage,
    AcvIconButton,
    AcvIcon,
  },
  directives: { onClickAway },
  data: () => ({
    mode: 'received',
    activeMessage: {},
    isPanelVisible: false,
    isSlidingAway: false, // New state to track slide-away transition
    timers: {
      autoSwitchTimer: null,
    },
  }),
  computed: {
    ...mapState({
      taunts: (state) => state.taunt.taunts,
    }),
    ...mapGetters({
      srpRefreshPreference: 'marketplaceView/srpRefreshPreference',
      showingFullPanel: 'taunt/showingFullPanel',
      isAuthorized: 'isAuthorized',
      currentUser: 'currentUser',
    }),
    hasActiveMessage() {
      return Object.keys(this.activeMessage).length && !this.isSlidingAway;
    },
    receivedTaunts() {
      return this.taunts.filter((taunt) => taunt.createdBy !== this.currentUser.id);
    },
    sentTaunts() {
      return this.taunts.filter((taunt) => taunt.createdBy === this.currentUser.id);
    },
    tauntsToShow() {
      if (this.mode === 'sent') {
        return this.sentTaunts;
      }
      if (this.mode === 'received') {
        return this.receivedTaunts;
      }

      return [];
    },
  },
  watch: {
    receivedTaunts: {
      deep: true,
      handler(newValue) {
        if (newValue.length) {
          this.updateActiveMessage();
        }
      },
    },
    showingFullPanel: {
      handler(newValue) {
        if (newValue) {
          if (this.activeMessage) {
            this.closeActiveMessage();
          }
          this.mode = 'received';
          this.isPanelVisible = newValue;
        } else {
          setTimeout(() => {
            this.isPanelVisible = newValue;
          }, 300); // wait for the transition to finish
        }
      },
    },
    isAuthorized: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!newValue && oldValue) {
          this.clearTimeouts();
          this.activeMessage = {};
        }
      },
    },
  },
  created() {
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.showingFullPanel) {
        this.handlePanelShow(false);
      }
    });
  },
  methods: {
    ...mapActions({
      handlePanelShow: 'taunt/handlePanelShow',
      removeTaunt: 'taunt/removeTaunt',
    }),
    updateActiveMessage() {
      if (this.showingFullPanel || !this.receivedTaunts.length) {
        this.clearAutoSwitchTimer();
        this.activeMessage = {};
        return;
      }

      const nextTaunt = this.receivedTaunts.filter((t) => t.timestamp <= Date.now() && !t.seen)[0];
      if (nextTaunt && nextTaunt?.id !== this.activeMessage?.id) {
        this.isSlidingAway = true;
        setTimeout(() => {
          this.activeMessage = nextTaunt;
          this.markAsSeen(nextTaunt.id);
          this.isSlidingAway = false;

          this.startAutoSwitchTimer();
        }, 300); // Match the slide-out animation duration
      }
    },
    startAutoSwitchTimer() {
      this.clearAutoSwitchTimer();
      this.timers.autoSwitchTimer = setTimeout(() => {
        this.clearAutoSwitchTimer();
        this.activeMessage = {};
      }, 10000); // Show each taunt for 10 seconds
    },
    clearAutoSwitchTimer() {
      if (this.timers.autoSwitchTimer) {
        clearTimeout(this.timers.autoSwitchTimer);
        this.timers.autoSwitchTimer = null;
      }
    },
    closeActiveMessage() {
      this.isSlidingAway = true;
      this.updateActiveMessage();
    },
    removeMessage(tauntId) {
      this.isSlidingAway = true;
      setTimeout(() => {
        this.isSlidingAway = false;
        this.removeTaunt(tauntId);
        this.clearAutoSwitchTimer();
        this.activeMessage = {};
      }, 300); // Match the slide-out animation duration
    },
    clearSpecificTimer(key) {
      if (this.timers[key]) {
        clearTimeout(this.timers[key]);
        this.timers[key] = null;
      }
    },
    clearTimeouts() {
      Object.keys(this.timers).forEach((key) => {
        this.clearSpecificTimer(key);
      });
    },
    markAsSeen(tauntId) {
      this.$store.dispatch('taunt/markTauntSeen', tauntId);
    },
    handleGoToAuction(auctionId) {
      this.isSlidingAway = true;
      this.handlePanelShow(false);
      this.$router.push({
        name: this.srpRefreshPreference ? 'Marketplace' : 'Search',
        params: { auctionDetailAuctionId: auctionId },
      });
    },
    handleClickAway() {
      if (this.showingFullPanel) {
        this.handlePanelShow(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.taunt-container {
  .taunt-active-message {
    position: fixed;
    top: 4.5rem;
    right: 1rem;
    z-index: 99999;
    border-radius: 8px;

    :deep(.taunt-message) {
      max-width: 500px;
      min-width: 300px;
    }
  }

  .taunt-full-panel {
    position: fixed;
    width: 30%;
    right: 0;
    background: theme('colors.background.white');
    height: 100%;
    transition: transform 0.3s ease-in-out;
    will-change: transform;
    padding: 1rem 1rem 1rem 1.5rem;
  }

  &--full-panel {
    position: fixed;
    height: 100%;
    width: 100%;
    inset: 0;
    z-index: 101;
    background: theme('colors.black.370');

    .taunt-full-panel {
      .taunt-modes {
        margin-top: 1rem;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        place-items: center;
        border-bottom: 1px solid theme('colors.border.base');

        button {
          display: flex;
          width: 100%;
          height: 56px;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: transparent;
          border: none;
          color: theme('colors.text.primary');
          border-radius: 0.75rem 0.75rem 0 0;

          &:hover {
            background: theme('colors.informational.light-x');
            cursor: pointer;
          }

          &:first-of-type {
            border-radius: 0.75rem 0.75rem 0 0;
          }

          &.selected {
            background: theme('colors.acv.primary.base');
            color: theme('colors.text.primary');
            color: white;
          }
        }
      }

      &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        h5 {
          margin: 0;
        }
      }

      .taunt-list {
        display: flex;
        max-height: calc(100vh - 160px);
        flex-direction: column;
        gap: 1rem;
        margin-top: 1rem;
        overflow-y: scroll;
      }
    }
  }
}

/* Update transition styles */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease-in-out;
}

.slide-right-enter {
  transform: translateX(100%);
}

.slide-right-leave-to {
  transform: translateX(100%);
}

/* Add transition styles for slide-side-panel */
.slide-side-panel-enter-active,
.slide-side-panel-leave-active {
  transition: transform 0.3s ease-in-out;
}

.slide-side-panel-enter,
.slide-side-panel-leave-to {
  transform: translateX(100%);
}

.slide-side-panel-enter-to,
.slide-side-panel-leave-from {
  transform: translateX(0%);
}

</style>
